import { useState, useEffect, useCallback } from 'react';
import {
  Page,
  Card,
  DataTable,
  Text,
  Banner,
  Spinner,
  TextField,
  Button,
  InlineStack,
  BlockStack,
  Modal,
  Badge,
  DropZone
} from '@shopify/polaris';

const DEFAULT_SETTINGS = {
  alert_margin_threshold: 20
};

function toNumber(value, fallback = 0) {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function parseCSV(text) {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return { headers: [], rows: [] };

  const parseRow = (line) => {
    const cols = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') { current += '"'; i++; }
        else { inQuotes = !inQuotes; }
      } else if (ch === ',' && !inQuotes) {
        cols.push(current.trim());
        current = '';
      } else {
        current += ch;
      }
    }
    cols.push(current.trim());
    return cols;
  };

  const headers = parseRow(lines[0]).map(h => h.toLowerCase().replace(/\s+/g, '_'));
  const rows = lines.slice(1).filter(l => l.trim()).map(line => {
    const cols = parseRow(line);
    return Object.fromEntries(headers.map((h, i) => [h, cols[i] ?? '']));
  });
  return { headers, rows };
}

export default function Products({ shop, shopData, showToast }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editCost, setEditCost] = useState('');
  const [saving, setSaving] = useState(false);

  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importRows, setImportRows] = useState([]);
  const [importHeaders, setImportHeaders] = useState([]);
  const [importError, setImportError] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);

  const settings = {
    ...DEFAULT_SETTINGS,
    ...(shopData?.settings || {})
  };

  const fetchProducts = useCallback(async () => {
    if (!shop) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`/api/products?shop=${shop}`);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || errorData.error || 'Failed to fetch products';
        throw new Error(errorMessage);
      }

      const data = await response.json();
      if (!Array.isArray(data)) {
        console.error('Invalid products response:', data);
        setProducts([]);
        showToast('Invalid response from server', true);
        return;
      }

      setProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
      showToast(error.message || 'Failed to load products', true);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [shop, showToast]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleOpenImport = () => {
    setImportFile(null);
    setImportRows([]);
    setImportHeaders([]);
    setImportError('');
    setImportResult(null);
    setImportOpen(true);
  };

  const handleFileDrop = useCallback((_dropFiles, acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;
    setImportFile(file);
    setImportError('');
    setImportResult(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      const { headers, rows } = parseCSV(e.target.result);
      if (!headers.includes('variant_id') || !headers.includes('cost')) {
        setImportError('CSV must have "variant_id" and "cost" columns. Download the template to get started.');
        setImportRows([]);
        return;
      }
      const valid = rows.filter(r => r.variant_id && r.cost !== '');
      setImportHeaders(headers);
      setImportRows(rows);
      if (valid.length === 0) {
        setImportError('No rows with a variant_id and cost found. Fill in the cost column and re-upload.');
      }
    };
    reader.readAsText(file);
  }, []);

  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const res = await fetch(`/api/products/export-template?shop=${shop}`);
      if (!res.ok) throw new Error('Failed to download template');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `profitpulse-products-template-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      showToast('Failed to download template', true);
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleImport = async () => {
    const validRows = importRows.filter(r => r.variant_id && r.cost !== '' && !isNaN(parseFloat(r.cost)));
    if (validRows.length === 0) {
      setImportError('No valid rows to import. Make sure the "cost" column is filled in.');
      return;
    }

    setImporting(true);
    setImportError('');
    try {
      const res = await fetch('/api/products/import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shop, rows: validRows.map(r => ({ variant_id: r.variant_id, cost: r.cost })) })
      });

      if (!res.ok) throw new Error('Import request failed');
      const data = await res.json();
      setImportResult(data);
      if (data.imported > 0) {
        fetchProducts();
        showToast(`Imported ${data.imported} product cost${data.imported === 1 ? '' : 's'}`);
      }
    } catch (err) {
      setImportError('Import failed. Please try again.');
    } finally {
      setImporting(false);
    }
  };

  const handleEditCost = (product) => {
    setEditingProduct(product);
    setEditCost(product.cost?.toString() || '');
  };

  const handleSaveCost = async () => {
    if (!editingProduct || editCost === '') return;

    setSaving(true);
    try {
      const response = await fetch('/api/products/cost', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shop,
          variantId: editingProduct.variantId,
          cost: parseFloat(editCost)
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update cost');
      }

      showToast('Cost updated successfully');
      setEditingProduct(null);
      setEditCost('');
      fetchProducts();
    } catch (error) {
      console.error('Error updating cost:', error);
      showToast('Failed to update cost', true);
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(toNumber(value));
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return '-';
    return `${toNumber(value).toFixed(1)}%`;
  };

  const getMarginTone = (margin) => {
    if (margin === null || margin === undefined) return 'info';
    if (margin < 0) return 'critical';
    if (margin < settings.alert_margin_threshold) return 'warning';
    return 'success';
  };

  const getMarginStatusLabel = (margin) => {
    if (margin === null || margin === undefined) return 'Needs cost';
    if (margin < 0) return 'Negative';
    if (margin < settings.alert_margin_threshold) return 'At risk';
    return 'Healthy';
  };

  const rows = products.map((product) => {
    const unitProfit = product.cost !== null && product.cost !== undefined
      ? toNumber(product.price) - toNumber(product.cost)
      : null;

    return [
      <Text variant="bodyMd" as="p" fontWeight="semibold">{product.productTitle}</Text>,
      product.variantTitle !== 'Default Title' ? product.variantTitle : '-',
      formatCurrency(product.price),
      <InlineStack gap="200" align="start">
        <Text as="span">{formatCurrency(product.cost)}</Text>
        {product.costSource === 'manual' && (
          <Badge tone="info">Manual</Badge>
        )}
      </InlineStack>,
      formatCurrency(unitProfit),
      <Badge tone={getMarginTone(product.margin)}>
        {formatPercent(product.margin)}
      </Badge>,
      <Badge tone={getMarginTone(product.margin)}>
        {getMarginStatusLabel(product.margin)}
      </Badge>,
      <Button size="slim" onClick={() => handleEditCost(product)}>
        Edit Cost
      </Button>
    ];
  });

  const productsWithCosts = products.filter((product) => product.cost !== null && product.cost !== undefined);
  const productsWithoutCosts = products.length - productsWithCosts.length;
  const lowMarginProducts = products.filter((product) => product.margin !== null && product.margin < settings.alert_margin_threshold);
  const avgMargin = productsWithCosts.length > 0
    ? productsWithCosts
        .filter((product) => product.margin !== null)
        .reduce((sum, product) => sum + toNumber(product.margin), 0) /
      Math.max(productsWithCosts.filter((product) => product.margin !== null).length, 1)
    : 0;
  const worstMarginProduct = [...lowMarginProducts].sort((a, b) => toNumber(a.margin) - toNumber(b.margin))[0];
  const cogsCoverageScore = products.length > 0
    ? Math.round((productsWithCosts.length / products.length) * 100)
    : 0;
  const cogsCoverageTone = cogsCoverageScore === 100 ? 'success' : cogsCoverageScore >= 60 ? 'warning' : 'critical';

  return (
    <Page
      title="Products"
      subtitle="Spot low-margin products before they become order-level profit leaks"
    >
      <BlockStack gap="400">
        <Card>
          <BlockStack gap="300">
            <InlineStack align="space-between" blockAlign="center">
              <Text variant="headingMd" as="h2">COGS Coverage Score</Text>
              <Badge tone={cogsCoverageTone}>
                {cogsCoverageScore}% covered
              </Badge>
            </InlineStack>
            <Text variant="bodySm" as="p" tone="subdued">
              {cogsCoverageScore < 100
                ? `${productsWithoutCosts} product${productsWithoutCosts === 1 ? '' : 's'} without costs — true profit is unreliable for those orders.`
                : 'All products have cost data. Profit calculations are reliable across your catalog.'}
            </Text>
            <InlineStack gap="600" wrap>
              <BlockStack gap="100">
                <Text variant="bodySm" tone="subdued">Total Products</Text>
                <Text variant="headingLg" as="p">{products.length}</Text>
              </BlockStack>
              <BlockStack gap="100">
                <Text variant="bodySm" tone="subdued">With Costs</Text>
                <Text variant="headingLg" as="p" tone="success">{productsWithCosts.length}</Text>
              </BlockStack>
              <BlockStack gap="100">
                <Text variant="bodySm" tone="subdued">Missing Costs</Text>
                <Text variant="headingLg" as="p" tone={productsWithoutCosts > 0 ? 'warning' : undefined}>
                  {productsWithoutCosts}
                </Text>
              </BlockStack>
              <BlockStack gap="100">
                <Text variant="bodySm" tone="subdued">Below Margin Threshold</Text>
                <Text variant="headingLg" as="p" tone={lowMarginProducts.length > 0 ? 'warning' : 'success'}>
                  {lowMarginProducts.length}
                </Text>
              </BlockStack>
              <BlockStack gap="100">
                <Text variant="bodySm" tone="subdued">Average Margin</Text>
                <Text variant="headingLg" as="p">{formatPercent(avgMargin)}</Text>
              </BlockStack>
            </InlineStack>
          </BlockStack>
        </Card>

        {productsWithoutCosts > 0 && (
          <Banner
            title={`${productsWithoutCosts} product(s) missing cost data`}
            tone="warning"
          >
            <p>Add costs so Profit Pulse AI can calculate true profit per order and trigger low-margin alerts accurately.</p>
          </Banner>
        )}

        {lowMarginProducts.length > 0 && worstMarginProduct && (
          <Banner
            title={`${lowMarginProducts.length} low-margin product(s) detected`}
            tone="warning"
          >
            <p>
              {worstMarginProduct.productTitle} is currently running at {formatPercent(worstMarginProduct.margin)}.
              Raise price, lower COGS, or exclude it from deep discounts to protect margin.
            </p>
          </Banner>
        )}

        <Card>
          <BlockStack gap="200">
            <Text variant="headingMd" as="h2">Bulk Import</Text>
            <Text as="p" tone="subdued">
              Download the pre-filled template, add your product costs in the Cost column, then re-upload it here.
            </Text>
            <InlineStack gap="200">
              <Button onClick={handleOpenImport} variant="primary">
                Import CSV
              </Button>
              <Button onClick={handleDownloadTemplate} loading={downloadingTemplate}>
                Download Template
              </Button>
            </InlineStack>
          </BlockStack>
        </Card>

        <Card>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <Spinner size="large" />
            </div>
          ) : products.length === 0 ? (
            <Banner>
              <p>No products found. Add products to Shopify first, then return here to protect their margins.</p>
            </Banner>
          ) : (
            <DataTable
              columnContentTypes={[
                'text',
                'text',
                'numeric',
                'text',
                'numeric',
                'text',
                'text',
                'text'
              ]}
              headings={[
                'Product',
                'Variant',
                'Price',
                'Cost',
                'Unit Profit',
                'Margin',
                'Status',
                'Action'
              ]}
              rows={rows}
              hoverable
            />
          )}
        </Card>
      </BlockStack>

      <Modal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title="Import Product Costs from CSV"
        primaryAction={importResult ? {
          content: 'Done',
          onAction: () => setImportOpen(false)
        } : {
          content: 'Import',
          loading: importing,
          disabled: importRows.filter(r => r.variant_id && r.cost !== '' && !isNaN(parseFloat(r.cost))).length === 0 || importing,
          onAction: handleImport
        }}
        secondaryActions={importResult ? [] : [{
          content: 'Cancel',
          onAction: () => setImportOpen(false)
        }]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {!importResult ? (
              <>
                <Text as="p" tone="subdued">
                  1. Download the template CSV prefilled with your products.{' '}
                  2. Fill in the <strong>cost</strong> column for each variant.{' '}
                  3. Upload it below and click <strong>Import</strong>.
                </Text>

                <Button onClick={handleDownloadTemplate} loading={downloadingTemplate}>
                  Download Template
                </Button>

                <DropZone
                  accept=".csv,text/csv"
                  type="file"
                  allowMultiple={false}
                  onDrop={handleFileDrop}
                  label="Upload CSV"
                >
                  {importFile ? (
                    <div style={{ padding: '1rem', textAlign: 'center' }}>
                      <Text as="p" fontWeight="semibold">{importFile.name}</Text>
                      <Text as="p" tone="subdued">
                        {importRows.length} row{importRows.length !== 1 ? 's' : ''} detected
                        {importRows.filter(r => r.variant_id && r.cost !== '' && !isNaN(parseFloat(r.cost))).length > 0 && (
                          <> — {importRows.filter(r => r.variant_id && r.cost !== '' && !isNaN(parseFloat(r.cost))).length} ready to import</>
                        )}
                      </Text>
                    </div>
                  ) : (
                    <DropZone.FileUpload actionTitle="Add CSV" actionHint="or drop a file here" />
                  )}
                </DropZone>

                {importFile && !importError && importRows.length > 0 && (
                  <BlockStack gap="200">
                    <Text variant="headingSm" as="h3">Preview (first 5 rows)</Text>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '13px' }}>
                        <thead>
                          <tr>
                            {['Product', 'Variant', 'Price', 'Cost'].map(h => (
                              <th key={h} style={{ textAlign: 'left', padding: '6px 10px', borderBottom: '1px solid #e1e3e5', fontWeight: 600 }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {importRows.slice(0, 5).map((row, i) => {
                            const hasValidCost = row.cost !== '' && !isNaN(parseFloat(row.cost));
                            return (
                              <tr key={i} style={{ background: !hasValidCost ? '#fff3cd' : undefined }}>
                                <td style={{ padding: '6px 10px', borderBottom: '1px solid #f1f1f1' }}>{row.product_title || '-'}</td>
                                <td style={{ padding: '6px 10px', borderBottom: '1px solid #f1f1f1' }}>{row.variant_title || '-'}</td>
                                <td style={{ padding: '6px 10px', borderBottom: '1px solid #f1f1f1' }}>{row.price ? `$${row.price}` : '-'}</td>
                                <td style={{ padding: '6px 10px', borderBottom: '1px solid #f1f1f1', color: hasValidCost ? '#008060' : '#bf0711' }}>
                                  {hasValidCost ? `$${parseFloat(row.cost).toFixed(2)}` : 'Missing'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {importRows.length > 5 && (
                      <Text as="p" tone="subdued">...and {importRows.length - 5} more rows</Text>
                    )}
                  </BlockStack>
                )}

                {importError && (
                  <Banner tone="critical">
                    <p>{importError}</p>
                  </Banner>
                )}
              </>
            ) : (
              <BlockStack gap="300">
                <Banner tone={importResult.errors > 0 && importResult.imported === 0 ? 'critical' : 'success'}>
                  <p>
                    <strong>{importResult.imported}</strong> product cost{importResult.imported !== 1 ? 's' : ''} imported successfully.
                    {importResult.skipped > 0 && <> {importResult.skipped} row{importResult.skipped !== 1 ? 's' : ''} skipped (missing or invalid cost).</>}
                    {importResult.errors > 0 && <> {importResult.errors} row{importResult.errors !== 1 ? 's' : ''} failed.</>}
                  </p>
                </Banner>
                {importResult.imported > 0 && (
                  <Text as="p" tone="subdued">
                    Your product list has been refreshed with the updated costs. Orders have been recalculated automatically.
                  </Text>
                )}
              </BlockStack>
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>

      {editingProduct && (
        <Modal
          open={!!editingProduct}
          onClose={() => setEditingProduct(null)}
          title="Edit Product Cost"
          primaryAction={{
            content: 'Save',
            loading: saving,
            onAction: handleSaveCost
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: () => setEditingProduct(null)
            }
          ]}
        >
          <Modal.Section>
            <BlockStack gap="300">
              <Text as="p">
                <strong>{editingProduct.productTitle}</strong>
                {editingProduct.variantTitle !== 'Default Title' && (
                  <> - {editingProduct.variantTitle}</>
                )}
              </Text>
              <Text as="p" tone="subdued">
                Current price: {formatCurrency(editingProduct.price)}
              </Text>
              <TextField
                label="Cost per unit"
                type="number"
                value={editCost}
                onChange={setEditCost}
                prefix="$"
                autoComplete="off"
                step="0.01"
                min="0"
                helpText="Include the product cost you pay before shipping, payment fees, and discounts hit the order."
              />
              {editCost && editingProduct.price && (
                <Text as="p" tone={
                  ((toNumber(editingProduct.price) - toNumber(editCost)) / toNumber(editingProduct.price) * 100) < 0
                    ? 'critical'
                    : ((toNumber(editingProduct.price) - toNumber(editCost)) / toNumber(editingProduct.price) * 100) < settings.alert_margin_threshold
                      ? 'warning'
                      : 'success'
                }>
                  Projected margin: {formatPercent(
                    (toNumber(editingProduct.price) - toNumber(editCost)) / toNumber(editingProduct.price) * 100
                  )}
                </Text>
              )}
            </BlockStack>
          </Modal.Section>
        </Modal>
      )}
    </Page>
  );
}
